import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchPolicyTransactions } from '@services/policy/policies';
import { PolicyTransactionStatus } from '@models/policyTransaction';
import { groupTransactionsByTerm } from '../utils/groupTransactionsByTerm';
import { PAGE_SIZE } from '../utils/constants';
import { PolicyTransactionItem } from '../types';

const ALLOWED_TRANSACTION_STATUSES = [PolicyTransactionStatus.Created, PolicyTransactionStatus.Processed];

interface UsePolicyTransactionsGroupedParams {
  policyId: string | undefined;
  enabled?: boolean;
}

export const usePolicyTransactionsGrouped = ({ policyId, enabled = true }: UsePolicyTransactionsGroupedParams) => {
  const [allTransactions, setAllTransactions] = useState<PolicyTransactionItem[]>([]);
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useSearchPolicyTransactions(
    {
      policyId: policyId || '',
      includeCanceled: false,
      pageLimit: PAGE_SIZE,
      pageOffset: page * PAGE_SIZE,
      status: ALLOWED_TRANSACTION_STATUSES,
      sort: '-effectiveOn,-createdAt',
    },
    enabled && !!policyId,
  );

  // Reset when policyId changes
  useEffect(() => {
    setAllTransactions([]);
    setPage(0);
  }, [policyId]);

  // Append new items when data changes
  useEffect(() => {
    if (!data?.items) return;

    const newItems = data.items;

    if (page === 0) {
      setAllTransactions(newItems);
    } else {
      setAllTransactions((prev) => {
        const combined = [...prev, ...newItems];
        const uniqueMap = new Map(combined.map((item) => [item.id, item]));
        return Array.from(uniqueMap.values());
      });
    }
  }, [data?.items, page]);

  const termGroups = useMemo(() => {
    return groupTransactionsByTerm(allTransactions);
  }, [allTransactions]);

  const totalCount = data?.pagination?.total || 0;
  const hasMore = allTransactions.length < totalCount;

  const loadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const reset = useCallback(() => {
    setPage(0);
    setAllTransactions([]);
  }, []);

  return {
    termGroups,
    transactions: allTransactions,
    isLoading: isLoading || isFetching,
    hasMore,
    loadMore,
    reset,
    totalCount,
  };
};
